export const DEFAULT_PAGE_SIZE = 5;

/** A page number to render, or a run of hidden pages to stand in for. */
export type PageSlot = number | "gap";

export function pageCount(itemCount: number, pageSize: number = DEFAULT_PAGE_SIZE): number {
	return Math.max(1, Math.ceil(itemCount / pageSize));
}

/**
 * Keep a page number inside the range that currently exists.
 *
 * Filtering a list shortens it under whatever page is open, which otherwise leaves the reader on
 * page 6 of 2 looking at an empty table and no way back.
 */
export function clampPage(page: number, itemCount: number, pageSize: number = DEFAULT_PAGE_SIZE): number {
	return Math.min(Math.max(1, page), pageCount(itemCount, pageSize));
}

export function sliceForPage<T>(items: T[], page: number, pageSize: number = DEFAULT_PAGE_SIZE): T[] {
	const start = (page - 1) * pageSize;
	return items.slice(start, start + pageSize);
}

/**
 * The page numbers worth drawing: the ends, the neighbourhood of the current page, and gaps.
 *
 * A gap has to hide more than one page to be worth its slot — standing an ellipsis where a single
 * number would fit costs the same room and takes away somewhere to click.
 */
export function pageWindow(current: number, totalPages: number, span = 1): PageSlot[] {
	const shown = new Set<number>([1, totalPages]);
	for (let page = current - span; page <= current + span; page++) {
		if (page >= 1 && page <= totalPages) shown.add(page);
	}

	const ordered = [...shown].sort((first, second) => first - second);
	const slots: PageSlot[] = [];

	ordered.forEach((page, index) => {
		const previous = ordered[index - 1];
		if (previous !== undefined) {
			const hidden = page - previous - 1;
			if (hidden === 1) slots.push(previous + 1);
			else if (hidden > 1) slots.push("gap");
		}
		slots.push(page);
	});

	return slots;
}

export interface PageRange {
	from: number;
	to: number;
}

/** The "showing X to Y" figures, counting from one and stopping at what is actually there. */
export function pageRange(page: number, itemCount: number, pageSize: number = DEFAULT_PAGE_SIZE): PageRange {
	if (itemCount === 0) return { from: 0, to: 0 };

	const from = (page - 1) * pageSize + 1;
	return { from, to: Math.min(page * pageSize, itemCount) };
}
